/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'
import { useRouter } from 'next/navigation'
import { ProvidersProps, UserListConfig } from '~types/index'

import {
  LoggedProvider,
  UseLoggedContext,
} from '@mercadoni/sinister/dist/cjs/authentication'
import {
  ListsProvider,
  UseListContext,
} from '@mercadoni/sinister/dist/cjs/shoppingLists'

import useGuessAction from '@/hooks/useGuessAction'
import { FALLBACK_CLIENT } from '@/utils/constants'

import uuid from '@/utils/uuid'
import { MessagesProvider } from '@mercadoni/sinister/dist/cjs/shared/contexts'
import { useEffect } from 'react'
import { DrawerControlProvider } from './DrawerControlContext'
import { GrocerProvider } from './GrocerContextV3'
import { CardTokenizationProvider } from './OPMContext'
import { OperationalModelContextProvider } from './OperationalModelContext'
import { RestrictedAgeProvider } from './RestrictedAgeContext'
import { StoreProvider, useStoreContext } from './StoreContext'

import { AddressProvider } from './AddressContext'
import { AuthContextProvider } from './AuthContext'
import CartProvider from './CartContext'
import { PreviousPathProvider } from './PreviousPathContext'
import { ReplacementsProvider } from './ReplacementsContext'

const FavoriteProvider = ({ children }: { children: React.ReactNode }) => {
  const { store } = useStoreContext()
  const { isLogged } = UseLoggedContext()
  const { favoriteService } = UseListContext()
  useEffect(() => {
    if (isLogged && store.reference && favoriteService.isEnabled) {
      favoriteService.getFavoriteSKUs(store.reference)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteService.isEnabled, isLogged, store.reference])

  return <>{children}</>
}

const ListsProviderMiddleware = ({
  children,
  listsConfig,
  clientId,
  currency,
}: {
  children: React.ReactNode
  listsConfig: UserListConfig
  clientId: string
  currency: string
}) => {
  const router = useRouter()
  const onGuessAction = useGuessAction({ router, forceToLogin: true })
  return (
    <ListsProvider
      listsConfig={listsConfig}
      clientId={clientId}
      currency={currency}
      onGuessAction={onGuessAction}
    >
      {children}
    </ListsProvider>
  )
}

export const EcommerceProviders = ({
  children,
  store,
  clientModel,
  operationalModel,
  storeConfigurations,
}: ProvidersProps) => {
  const {
    clientId = FALLBACK_CLIENT,
    lists = { favorite: { display: false } },
    currencyCode = '',
    authentication,
    signUpConfig,
    site,
  } = clientModel

  const { fullMigrated } = authentication || {}

  let storedAddress = {}
  try {
    storedAddress =
      typeof window !== 'undefined'
        ? JSON.parse(window?.localStorage?.getItem('selectedAddress') || '{}')
        : {}
  } catch (error) {
    console.log('� ~ Error: GET_STORED_ADDRESS', error)
  }

  return (
    <LoggedProvider
      authentication={authentication}
      uuid={uuid()}
      signUpConfig={signUpConfig}
      site={site}
    >
      <AuthContextProvider
        clientId={clientId}
        fbConfig={clientModel.FB_CONFIG}
        federatedClient={fullMigrated || false}
      >
        <ReplacementsProvider
          clientConfig={clientModel}
          configurations={storeConfigurations}
        >
          <RestrictedAgeProvider>
            <ListsProviderMiddleware
              listsConfig={lists}
              clientId={clientId}
              currency={currencyCode}
            >
              <PreviousPathProvider>
                <StoreProvider clientId={clientId} store={store}>
                  <OperationalModelContextProvider
                    operationalModel={operationalModel}
                  >
                    <AddressProvider address={storedAddress}>
                      <CartProvider clientModel={clientModel}>
                        <GrocerProvider>
                          <CardTokenizationProvider clientModel={clientModel}>
                            <DrawerControlProvider>
                              <MessagesProvider>
                                <FavoriteProvider>{children}</FavoriteProvider>
                              </MessagesProvider>
                            </DrawerControlProvider>
                          </CardTokenizationProvider>
                        </GrocerProvider>
                      </CartProvider>
                    </AddressProvider>
                  </OperationalModelContextProvider>
                </StoreProvider>
              </PreviousPathProvider>
            </ListsProviderMiddleware>
          </RestrictedAgeProvider>
        </ReplacementsProvider>
      </AuthContextProvider>
    </LoggedProvider>
  )
}
